<template>
  <div>
    <div v-if="requirement">
      <div>
        <h3>{{ requirement.name }}</h3>
      </div>
      <edit-requirement-modal :id="id"></edit-requirement-modal>
      <move-requirement-modal :requirements="[requirement]" modalId="move-requirement-modal-RequirementsDetail"></move-requirement-modal>
      <delete-requirement-modal :requirements="[requirement]" modalId="delete-requirement-modal-RequirementsDetail"></delete-requirement-modal>
      <preview-image-modal :modalImageUrl="modalImageUrl" :modalImageFilename="modalImageFilename"></preview-image-modal>
      <div class="detail-view-buttons">
        <b-button variant="secondary" size="sm" v-b-modal.edit-requirement-modal>
          <icon name="pencil"></icon>
          Edit
        </b-button>
        <b-button variant="warning" size="sm" v-b-modal.move-requirement-modal-RequirementsDetail>
          Move
        </b-button>
        <b-button variant="danger" size="sm" v-b-modal.delete-requirement-modal-RequirementsDetail>
          Delete
        </b-button>
      </div>
      <div class="card mt-2">
        <div class="card-body">
          <h4 class="card-title">Details</h4>
          <div class="card-text">
            <table class="table table-sm requirement-detail-table">
              <tbody>
              <tr>
                <th scope="row">ID:</th>
                <td>{{ requirement.requirement_type.prefix }}{{ requirement.display_id ? requirement.display_id : requirement.requirement_id }}</td>
              </tr>
              <tr>
                <th scope="row">Type:</th>
                <td>{{ requirement.requirement_type.name }} Requirement</td>
              </tr>
              <tr>
                <th scope="row">Created:</th>
                <!--Add 'Z' here because get requirement returns the string of the UTC date without timezone data, while the revision one just returns created_at, which i think gets the string of the datetime and therefore includes GMT-->
                <td>{{ getHumanDate(requirement.created_at + 'Z') }}</td>
              </tr>
              <tr>
                <th scope="row">Reporter:</th>
                <td v-if="reporter">
                  {{ reporter.displayName }}
                </td>
                <td class="help-text" v-else>None</td>
              </tr>
              <tr v-if="!currentProjectIsGlobal">
                <th scope="row">Assignee:</th>
                <td v-if="assignee">
                  {{ assignee.displayName }}
                </td>
                <td class="help-text" v-else>None</td>
              </tr>
              <tr v-if="!currentProjectIsGlobal">
                <th scope="row">Status:</th>
                <td v-if="status">
                  <b-badge pill variant="primary" >{{ status.name }} </b-badge>
                </td>
                <td class="help-text" v-else>None</td>
              </tr>
              <tr v-if="!currentProjectIsGlobal">
                <th scope="row">Component(s):</th>
                <td v-if="selectedComponents.length > 0">
                  <template v-for="component in selectedComponents">
                    <b-badge pill variant="secondary" >{{ component }} </b-badge>
                    <slot> </slot>
                  </template>
                </td>
                <td class="help-text" v-else>None</td>
              </tr>
              <tr v-for="attribute in attributes">
                <th scope="row">{{attribute.name}}:</th>
                <td v-if="requirement.attributes[attribute.attribute_id]">
                  {{ getCustomAttributeValue(attribute, requirement.attributes) }}
                </td>
                <td class="help-text" v-else>None</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="card mt-2">
        <div class="card-body">
          <h4 class="card-title">Description</h4>
          <div class="card-text">
            <b-form-textarea v-if="requirement.description"
                             plaintext
                             :max-rows="6"
                             :value="requirement.description">
            </b-form-textarea>
            <span class="help-text" v-else>Click "Edit" above to add a description</span>
          </div>
        </div>
      </div>
      <div class="card mt-2">
        <div class="card-body">
          <h4 class="card-title">Linked requirements</h4>
          <div class="card-text">
            <table class="table table-sm requirement-detail-table" v-if="selectedRequirements.length > 0">
              <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Name</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="requirement in selectedRequirements">
                <th scope="row"><a href="#" @click="goToReq(requirement.requirement_id)">{{ requirement.requirement_type.prefix }}{{requirement.display_id ? requirement.display_id : requirement.requirement_id}}</a></th>
                <td>{{ requirement.name }}</td>
              </tr>
              </tbody>
            </table>
            <span class="help-text" v-else>Click "Edit" above to link requirements</span>
          </div>
        </div>
      </div>
      <div class="card mt-2">
        <div class="card-body">
          <h4 class="card-title">Linked Issues</h4>
          <div class="card-text">
            <table class="table table-sm requirement-detail-table" v-if="selectedIssues.length > 0">
              <thead></thead>
              <tbody>
              <tr v-for="issue in selectedIssues">
                <th scope="row"><a target="_blank" :href="`${$auth.user().atlassian_base_url}/browse/${issue.key}`">{{ issue.key }}</a></th>
                <td>{{ issue.fields.summary }}</td>
                <td><img :src="issue.fields.issuetype.iconUrl"/></td>
              </tr>
              </tbody>
            </table>
            <span class="help-text" v-else>Click "Edit" above to link issues</span>
          </div>
        </div>
      </div>
      <div class="card mt-2" v-if="$auth.user().installed_on_confluence">
        <div class="card-body">
          <h4 class="card-title">Linked Confluence Content</h4>
          <div class="card-text">
            <table class="table table-sm requirement-detail-table" v-if="selectedContents.length > 0">
              <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col">Last Updated</th>
                <th scope="col">Contributors</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="content in selectedContents">
                <th scope="row"><a target="_blank" :href="`${$auth.user().atlassian_base_url}/wiki${content._links.webui}`">{{ content.title }}</a></th>
                <td>{{ content.history.lastUpdated.friendlyWhen }}</td>
                <td>
                <span v-for="(displayName, index) in content.history.contributors.publishers.users.map(a => a.displayName)">
                  <span>{{displayName}}</span><span v-if="index+1 < content.history.contributors.publishers.users.map(a => a.displayName).length">, </span>
                </span>
                </td>
              </tr>
              </tbody>
            </table>
            <span class="help-text" v-else>Click "Edit" above to link Confluence content</span>
          </div>
        </div>
      </div>
      <div class="card mt-2">
        <div class="card-body">
          <h4 class="card-title">Attachments</h4>
          <div class="card-text">
            <table class="table table-sm requirement-detail-table">
              <tbody>
              <tr v-for="attachment in requirement.attachments">
                <td>{{ attachment.original_file_name }}</td>
                <td><span v-if="isImageFilename(attachment.original_file_name)" @click="previewFile(attachment)" class="button-need-pointer" title="Preview"><icon name="search-plus"></icon></span></td>
                <td><span @click="downloadFile(attachment)" class="button-need-pointer" title="Download"><icon name="download"></icon></span></td>
                <td><span @click="deleteFile(attachment)" class="button-need-pointer" title="Delete"><icon name="trash-o"></icon></span></td>
              </tr>
              </tbody>
            </table>
            <input type="file" @change="processFile($event)" id="file_input" ref="fileInput"/>
          </div>
        </div>
      </div>
      <div class="card mt-2">
        <div class="card-body">
          <h4 class="card-title">Activity</h4>
          <div class="card-text">
            <table class="table table-sm requirement-detail-table">
              <thead>
              <tr>
                <th scope="col">Revision</th>
                <th scope="col">Date</th>
                <th scope="col">User</th>
                <th scope="col">Action</th>
                <th scope="col">Changed Fields</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(revision, index) in revisions">
                <th scope="row">{{ index }}</th>
                <td>{{ getHumanDate(revision.created_at) }}</td>
                <td v-if="userLookup[revision.account_id]">{{ userLookup[revision.account_id].displayName }}</td>
                <td class="help-text" v-else>None</td>
                <td>{{ revision.action }}</td>
                <td v-if="revision.changed_fields">
                  <template v-for="changedField in revision.changed_fields.split(',')">
                    <b-badge pill variant="info" >{{ changedField }} </b-badge>
                    <slot> </slot>
                  </template>
                </td>
                <td class="help-text" v-else>None</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="loading">
      <div class="d-flex justify-content-center align-items-center">
        <icon  name="spinner" pulse scale="5"></icon>
      </div>
    </div>
  </div>
</template>


<script>
  import dayjs from 'dayjs'
  import advancedFormat from 'dayjs/plugin/advancedFormat'
  dayjs.extend(advancedFormat)
  import CreateRequirementModal from '@/components/modals/CreateRequirementModal'
  import DeleteRequirementModal from '@/components/modals/DeleteRequirementModal'
  import EditRequirementModal from '@/components/modals/EditRequirementModal'
  import MoveRequirementModal from '@/components/modals/MoveRequirementModal'
  import PreviewImageModal from '@/components/modals/PreviewImageModal'
  import getDataFromAtlassianMixin from '@/mixins/getDataFromAtlassian'
  import uploadToS3Mixin from '@/mixins/uploadToS3'
  import utilsMixin from '@/mixins/utils'
  export default {
    components: {
      CreateRequirementModal,
      DeleteRequirementModal,
      EditRequirementModal,
      MoveRequirementModal,
      PreviewImageModal
    },
    data () {
      return {
        assignee: null,
        attributes: [],
        loading: false,
        modalImageFilename: null,
        modalImageUrl: null,
        selectedComponents: [],
        selectedComponentsFull: [],
        selectedContents: [],
        selectedIssues: [],
        selectedRequirements: [],
        reporter: null,
        requirement: null,
        revisions: [],
        status: null,
        userLookup: {},
        userAccountIdsToQuery: new Set()
      }
    },
    computed: {
      currentProject () {
        return this.$store.state.currentProject
      },
      currentProjectIsGlobal () {
        return this.currentProject && this.currentProject.id === 0
      },
      needToRefreshRequirementsDetail () {
        return this.$store.state.needToRefreshRequirementsDetail
      }
    },
    watch: {
      needToRefreshRequirementsDetail (currentValue) {
        if (currentValue === true) {
          // Refresh then set the value to be false
          this.refreshData()
          this.$store.commit('setNeedToRefreshRequirementsDetail', false)
        }
      }
    },
    props: {
      id: Number
    },
    created () {
      if (this.id) {
        this.refreshData()
      }
      this.$watch('id', this.refreshData)
    },
    mixins: [getDataFromAtlassianMixin, uploadToS3Mixin, utilsMixin],
    methods: {
      clearFileInput () {
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = null
        }
      },
      downloadFile (attachment) {
        this.$http.post(
          '/api/sign-s3-get',
          {
            original_file_name: attachment.original_file_name,
            uuid_file_name: attachment.uuid_file_name
          }
        ).then(response => {
          this.triggerDownload(response.data.presigned_url)
        }, response => {
        })
      },
      deleteFile (attachment) {
        this.requirement.attachments.splice(this.requirement.attachments.findIndex(x => x.uuid_file_name === attachment.uuid_file_name), 1)
        this.updateAttachment('delete')
      },
      previewFile (attachment) {
        this.$http.post(
          '/api/sign-s3-get',
          {
            original_file_name: attachment.original_file_name,
            uuid_file_name: attachment.uuid_file_name
          }
        ).then(response => {
          this.modalImageUrl = response.data.presigned_url
          this.modalImageFilename = response.data.original_file_name
          this.$root.$emit('bv::show::modal', 'image-modal')
        }, response => {
          window.AP.flag.create({
            title: 'Error',
            body: 'Error previewing attachment. Please contact rmCloud support via the "Get help" button',
            type: 'error',
            close: 'auto'
          })
        })
      },
      triggerDownload (url) {
        let link = document.createElement('a')
        document.body.appendChild(link)
        link.download = 'test.png'
        link.href = url
        link.click()
      },
      getAssigneeDataFromAtlassian () {
        if (this.requirement.assigned_to) {
          window.AP.request({
            url: `/rest/api/3/user?accountId=${this.requirement.assigned_to.account_id}`,
            success: response => {
              // convert the string response to JSON
              response = JSON.parse(response)
              this.assignee = response
            },
            error: () => {
              console.log(arguments)
            }
          })
        } else {
          this.assignee = null
        }
      },
      getReporterDataFromAtlassian () {
        // TODO - factor out this user call and the one above.  had trouble assigning the result to a variable
        if (this.requirement.created_by) {
          window.AP.request({
            url: `/rest/api/3/user?accountId=${this.requirement.created_by.account_id}`,
            success: response => {
              // convert the string response to JSON
              response = JSON.parse(response)
              this.reporter = response
            },
            error: () => {
              console.log(arguments)
            }
          })
        } else {
          this.reporter = null
        }
      },
      getHumanDate (date) {
        return dayjs(date).format('MMMM Do YYYY, h:mm:ss a')
      },
      getIssueIds (issueObjArray) {
        return issueObjArray.map(d => { return d['atlassian_issue_id'] })
      },
      getSelectedComponentDataFromAtlassian () {
        if (this.requirement.components.length > 0) {
          window.AP.request({
            url: `/rest/api/3/project/${this.currentProject.id}/components`,
            success: response => {
              // convert the string response to JSON
              response = JSON.parse(response)
              let componentIdList = this.requirement.components.map(a => a.atlassian_component_id.toString())
              // First filter down by ID, then only get the names
              // Keep this full list to be able to pass back to rmCloud to edit the requirement
              this.selectedComponentsFull = response.filter(a => componentIdList.includes(a.id))
              this.selectedComponents = this.selectedComponentsFull.map(a => a.name)
            },
            error: () => {
              console.log(arguments)
            }
          })
        } else {
          this.selectedComponents = []
        }
      },
      getselectedContentsData () {
        this.selectedContents = []
        if (this.requirement.contents.length > 0) {
          this.$http.post(
            '/api/confluence/content/search',
            {contents: this.requirement.contents}
          ).then(response => {
            this.selectedContents = response.data
          })
        }
      },
      getStatusDataFromAtlassian () {
        if (this.requirement.status) {
          window.AP.request({
            url: `/rest/api/3/status/${this.requirement.status.atlassian_status_id}`,
            success: response => {
              // convert the string response to JSON
              response = JSON.parse(response)
              this.status = response
            },
            error: () => {
              console.log(arguments)
            }
          })
        } else {
          this.status = null
        }
      },
      getSelectedIssueDataFromAtlassian () {
        this.selectedIssues = []
        if (this.requirement.issues.length > 0) {
          this.getIssueDataFromAtlassianPaginated(this.getIssueIds(this.requirement.issues), null, 50)
        }
      },
      getRequirement () {
        this.$http.get(
          '/api/requirement/' + this.id
        ).then(response => {
          this.requirement = response.data.requirement
          this.selectedRequirements = response.data.requirement.linked_requirements_v2
          this.loading = false
          // TODO - Use a Promise.all() here to set loading to false after all of this shit loads.
          this.getRevisions()
          this.getAssigneeDataFromAtlassian()
          this.getReporterDataFromAtlassian()
          this.getselectedContentsData()
          this.getSelectedIssueDataFromAtlassian()
          this.getSelectedComponentDataFromAtlassian()
          this.getStatusDataFromAtlassian()
          this.getAttributes()
        }, response => {
          console.log('error')
          this.loading = false
        })
      },
      getAttributes () {
        this.attributesLoading = true
        this.$http.get(
          `api/attribute?atlassian_project_id=${this.currentProject.id}`
        ).then(response => {
          this.attributes = response.data.attributes
        }, response => {
          window.AP.flag.create({
            title: 'Error',
            body: 'There was a problem loading custom attributes.  Please contact rmCloud support via the "Get help" button',
            type: 'error'
          })
        })
      },
      getCustomAttributeValue (attributeDef, attributes) {
        // We either store the raw value for a string custom attribute type, or the option ID for a select type
        // If it's select type, return the name of the option
        // Else, it must be a string type, so just return the raw value
        const rawValue = attributes[attributeDef.attribute_id.toString()]
        if (attributeDef.type === 'select') {
          for (let option of attributeDef.options) {
            if (option.attribute_option_id === rawValue) {
              return option.name
            }
          }
          return 'Error retrieving field data from rmCloud.  Please contact support with the "Get help" button below'
        }
        return rawValue
      },
      getRevisions () {
        this.$http.get(
          '/api/requirement/' + this.requirement.requirement_id + '/revision'
        ).then(response => {
          this.revisions = response.data.revisions
          this.getRevisionUserDataFromAtlassian()
        }, response => {
          console.log('error')
        })
      },
      getRevisionUserDataFromAtlassian () {
        this.revisions.forEach(revision => {
          this.userAccountIdsToQuery.add(revision.account_id)
        })
        this.userAccountIdsToQuery.forEach(accountId => {
          window.AP.request({
            url: `/rest/api/3/user?accountId=${accountId}`,
            success: response => {
              // convert the string response to JSON
              response = JSON.parse(response)
              this.$set(this.userLookup, accountId, response)
            },
            error: () => {
              console.log(arguments)
            }
          })
        })
      },
      getSignedRequest (file) {
        // TODO 3/2020 put logic from other sign-s3-put call for empty file.type
        this.$http.post(
          '/api/sign-s3-put',
          {
            operation: 'attachment',
            original_file_name: file.name,
            file_type: file.type
          }
        ).then(response => {
          // TODO 5/3/2020 - make a map of common file extensions and types to get the file.type in case it's missing
          // Check if the file.type is empty (can happen sometimes, see https://stackoverflow.com/questions/51724649/mime-type-of-file-returning-empty-in-javascript-on-some-machines)
          // It's 100% based on the filename anyway, so I can just hardcode text/csv if needed.
          this.uploadFile(file, file.type, response.data.presigned_url, response.data.uuid_file_name)
          this.requirement.attachments.push({
            original_file_name: file.name,
            uuid_file_name: response.data.uuid_file_name
          })
          this.updateAttachment('add')
        }, response => {
        })
      },
      goToReq (requirementId) {
        window.AP.history.pushState(`${this.currentProject.id}-requirements-${requirementId}`)
      },
      isImageFilename (filename) {
        const imageFileTypes = new Set(['png', 'jpeg', 'jpg'])
        let fileExt = filename.split('.').pop().toLowerCase()
        return imageFileTypes.has(fileExt)
      },
      processFile (event) {
        let file = event.target.files[0]
        if (!file) {
          return alert('No file selected.')
        }
        if (file.size > 1024 * 1024 * 25) {
          this.clearFileInput()
          window.AP.flag.create({
            title: 'Error',
            body: 'Error adding attachment: File size cannot exceed 25MB',
            type: 'error',
            close: 'auto'
          })
        } else {
          this.getSignedRequest(file)
        }
      },
      refreshData () {
        this.loading = true
        this.requirement = null
        this.userLookup = {}
        this.getRequirement()
        this.clearFileInput()
      },
      setIssues (response, readyToRender) {
        this.selectedIssues.push(...response.issues)
      },
      updateAttachment (operation) {
        let successMessage = operation === 'add' ? 'Attachment added successfully' : 'Attachment deleted successfully'
        let errorMessage = operation === 'add' ? 'There was a problem adding your attachment:' : 'There was a problem deleting your attachment:'
        this.$http.patch(
          '/api/requirement/' + this.requirement.requirement_id,
          {
            attachments: this.requirement.attachments
          }
        ).then(response => {
          window.AP.flag.create({
            title: 'Success!',
            body: successMessage,
            type: 'success',
            close: 'auto'
          })
          this.refreshData()
        }, error => {
          let errorString = this.createErrorStringFromResponse(error.response.data)
          window.AP.flag.create({
            title: 'Error',
            body: `${errorMessage} ${errorString}`,
            type: 'error',
            close: 'auto'
          })
        })
      }
    }
  }
</script>

<style>
  .detail-view-buttons {
    margin-bottom: 0.5rem;
  }
  .button-need-pointer {
    cursor: pointer;
  }
  .card-body .card-text .requirement-detail-table {
    margin-bottom: 0;
  }
  .help-text {
    font-style: italic;
    color: #999;
  }
</style>



// WEBPACK FOOTER //
// RequirementsDetail.vue?70ff5a48